import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from 'antd';
import ConfluenceArticle from '@components/UI/ConfluenceArticle';
import useRuntimeConfig from '@vl-core/hooks/useConfig';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '@actions';
import styled from '@emotion/styled';
import { InfoModal } from 'vl-common/src/components/Modals';

interface WarningProps {
  closable?: boolean;
  mode?: 'full' | 'default';
  isClinician?: boolean;
  showWithoutAppointment?: boolean;
  isWaitingRoom?: boolean;
  startTesting?: () => void;
}

const StyledPermissionsCheckContainer = styled.div<Pick<WarningProps, 'mode'>>`
  width: 100%;

  ${({ mode }) => {
    if (mode === 'full') return 'margin-bottom: 20px;';

    return `
      max-width: 1024px;
      margin: 10px 20px;

      @media (max-width: 1096px) {
        margin: 10px 0px;
      }
      `;
  }}}

  .permissions-alert {
    width: 100%;
  }

  .notifications-text {
    margin-right: 10px;
  }
`;

// urlB64ToUint8Array is a magic function that will encode the base64 public key
// to Array buffer which is needed by the subscription option
const urlB64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const checkPermissionsWithPermissionsAPI = async (permissionToCheck) => {
  const permission = await navigator?.permissions?.query({ name: permissionToCheck as PermissionName });

  return permission.state === 'granted';
};

/**
 * Enumerates available devices, checks the kind (videoinput for camera, audioinput for microphone)
 * and if there is a label (no label means permission not granted)
 * @returns { camera: boolean, microphone: boolean }
 */
const checkPermissionsWithEnumerateDevices = async () => {
  const devices = await navigator.mediaDevices.enumerateDevices();

  return {
    camera: !!devices.find((device) => device.kind === 'videoinput' && device.label),
    microphone: !!devices.find((device) => device.kind === 'audioinput' && device.label)
  };
};

/**
 * Firefox doesn't support the permissions API for camera / microphone.
 * So we need to check if it exists before using it - if not we use the
 * outdated enumerateDevices to check for available audio/video input devices
 */
const checkPermissions = async () => {
  let cameraPerm = false;
  let microphonePerm = false;
  if (navigator?.permissions?.query) {
    try {
      cameraPerm = await checkPermissionsWithPermissionsAPI('camera');
      microphonePerm = await checkPermissionsWithPermissionsAPI('microphone');
    } catch {
      const result = await checkPermissionsWithEnumerateDevices();

      cameraPerm = result.camera;
      microphonePerm = result.microphone;
    }
  } else if (navigator?.mediaDevices?.enumerateDevices) {
    const result = await checkPermissionsWithEnumerateDevices();

    cameraPerm = result.camera;
    microphonePerm = result.microphone;
  }

  return {
    camera: cameraPerm,
    microphone: microphonePerm
  };
};

const unregisterNotificationsServiceWorker = async () => {
  const registrations = await navigator?.serviceWorker?.getRegistrations();

  if (registrations) {
    registrations.forEach((reg) => {
      // registration.unregister();
      if (reg.active?.scriptURL.includes('notifications')) {
        reg.unregister();
      }
    });
  }
};

const checkNotifications = (): { permissionGranted: boolean; supported?: boolean } => {
  if (!Notification) {
    unregisterNotificationsServiceWorker();
    return { permissionGranted: false, supported: false };
  }
  if (Notification?.permission === 'granted') {
    // Fine - try to register service worker?
    return { permissionGranted: true };
  }
  if (Notification?.permission !== 'denied') {
    return { permissionGranted: false, supported: true };
  }

  unregisterNotificationsServiceWorker();
  return { permissionGranted: false };
};

const checkSubscribed = async (swReg) => {
  const subscription = await swReg.pushManager.getSubscription();
  return subscription !== null;
};

const PermissionsCheckWarning = ({
  closable,
  mode = 'default',
  isClinician = false,
  showWithoutAppointment = false,
  isWaitingRoom = false,
  startTesting
}: WarningProps) => {
  const { t } = useTranslation('common');
  const [hasCameraPermission, setHasCameraPermission] = useState(false);
  const [hasMicrophonePermission, setHasMicrophonePermission] = useState(false);
  const [hasUpcomingVideoAppointment, setHasUpcomingVideoAppointment] = useState(false);
  const [hasNotificationsPermission, setHasNotificationsPermission] = useState(false);
  const [promptForNotifications, setPromptForNotifications] = useState(false);
  const [permissionsChecked, setPermissionsChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { VAPID_PUBLIC } = useRuntimeConfig();
  const { upcomingHistory: allOnlineAppointments } = useSelector((state) => state.auth);

  const dispatch = useDispatch();

  useEffect(() => {
    if (showWithoutAppointment) {
      setHasUpcomingVideoAppointment(true);
      return;
    }
    if (allOnlineAppointments?.length) {
      const apptIsVideoCall = allOnlineAppointments.some((appt) => {
        return appt.call_type_code === 'VIDEO';
      });

      setHasUpcomingVideoAppointment(apptIsVideoCall);
    } else {
      setHasUpcomingVideoAppointment(false);
    }
  }, [allOnlineAppointments, showWithoutAppointment]);

  useEffect(() => {
    const checkForPermissions = async () => {
      const { camera, microphone } = await checkPermissions();
      const { permissionGranted, supported } = checkNotifications();

      setHasCameraPermission(camera);
      setHasMicrophonePermission(microphone);
      setHasNotificationsPermission(permissionGranted);
      setPromptForNotifications(isClinician ? false : supported);
      setPermissionsChecked(true);
    };

    checkForPermissions();
  }, []);

  const onAllowNotificationsClick = async () => {
    const permission = await Notification?.requestPermission();
    if (permission && permission === 'granted' && 'serviceWorker' in navigator && 'PushManager' in window) {
      const swReg = await navigator.serviceWorker
        ?.register('/notifications-sw.js')
        .then((registration) => {
          registration.update().then();
          return registration;
        })
        .catch(() => {
          console.error(
            'Failed to register notifications-sw.js. Have you built the service-worker-notifications directory?'
          );
        });
      setHasNotificationsPermission(true);
      if (swReg) {
        const subscribed = await checkSubscribed(swReg);

        if (!subscribed) {
          const applicationServerKey = urlB64ToUint8Array(VAPID_PUBLIC);
          const options = { applicationServerKey, userVisibleOnly: true };
          const subscription = await swReg.pushManager.subscribe(options);

          await dispatch(
            actions.pushNotificationSubscription({
              subscription: JSON.stringify(subscription)
            })
          );
        }
      }
    } else if (!permission || permission !== 'granted') {
      unregisterNotificationsServiceWorker();
      setHasNotificationsPermission(false);
      setPromptForNotifications(false);
    }
  };

  if (
    (hasCameraPermission &&
      hasMicrophonePermission &&
      (hasNotificationsPermission || (!hasNotificationsPermission && !promptForNotifications))) ||
    !permissionsChecked ||
    !hasUpcomingVideoAppointment
  )
    return <></>;

  return (
    <StyledPermissionsCheckContainer className="permissions-alert-container" mode={mode}>
      <Alert
        message={t('For your upcoming appointment')}
        type="info"
        showIcon
        closable={closable || isWaitingRoom}
        className={`permissions-alert ${mode}`}
        description={
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {(!hasCameraPermission || !hasMicrophonePermission) && (
              <span>
                {t('You need to allow access to your camera and microphone.')}{' '}
                {!isWaitingRoom ? (
                  <>
                    {t('To avoid delays, please login 5-10 mins early, enter the waiting room and look f')}{' '}
                    <b>{t('Test call')}</b> {t(' button.')}
                  </>
                ) : (
                  <>
                    {t('To avoid delays, please look for the ')}
                    <b>{t('Test Call')}</b> {t(' button to allow these before the appointment starts.')}{' '}
                    <Button type="primary" onClick={startTesting} data-testid="test-call-button">
                      {t('Test call')}
                    </Button>
                  </>
                )}
              </span>
            )}
            {!hasNotificationsPermission && promptForNotifications && (
              <div>
                <span className="notifications-text">
                  {t('You can also opt-in to receive notifications when a clinician is trying to call')}{' '}
                  <Button type="primary" onClick={onAllowNotificationsClick} data-testid="allow-notifications-button">
                    {t('Allow notifications')}
                  </Button>
                </span>
              </div>
            )}
            {(!hasCameraPermission || !hasMicrophonePermission) && (
              <span>
                {t('Unsure?')}{' '}
                <Button
                  data-testid="read-support-button"
                  type="link"
                  onClick={() => {
                    setShowModal(true);
                  }}
                  tabIndex={0}
                >
                  {t('Read our support article here')}
                </Button>
              </span>
            )}
          </div>
        }
      />
      <InfoModal
        open={showModal}
        data-testid="permissions-check-article-modal"
        onCancel={() => {
          setShowModal(false);
        }}
        footer={[
          <Button
            key="close"
            data-testid="permissions-check-modal-close-button"
            onClick={() => {
              setShowModal(false);
            }}
          >
            {t('Close')}
          </Button>
        ]}
      >
        <ConfluenceArticle id="CONF_BROWSER_PERMS" />
      </InfoModal>
    </StyledPermissionsCheckContainer>
  );
};

export default PermissionsCheckWarning;
